// Picks a readable "Investing Essentials" title colour for a festive
// background image, so the backend never has to send one.
//
// It averages the brightness (Rec.601 luma, 0-255) of the image area behind
// the heading and switches to dark text only when that area is clearly light.
// A plain "higher WCAG contrast wins" rule is not used on purpose: it picks
// dark text on mid-tone gold (the Diwali image measures ~142), while the
// design wants white there. Measured heading zones: gold 142-146, red 74,
// maroon 51, navy 59 -> white; pastel 216, cream 231-246 -> dark.
//
// Reading pixels needs the image to be served with CORS headers and loaded
// with crossOrigin="anonymous"; otherwise the canvas is tainted and this
// returns null (the caller keeps its default colour).

export const LIGHT_BG_TITLE_COLOR = "#111827";
export const DARK_BG_TITLE_COLOR = "#FFFFFF";

const DARK_TITLE_LUMA = 190;
const SAMPLE_WIDTH = 240;

// Heading zones from the background size templates, as fractions.
// Desktop: the image is shown at full section width and pinned to the top,
// so vertical positions scale with the image WIDTH (1440-wide canvas:
// x 0-700, y 40-180). Mobile: the section takes the image's aspect ratio,
// so they scale with its HEIGHT (375x329 canvas: x 0-245, y 8-40).
const HEADING_ZONE = {
  desktop: { x1: 0.48, y0OfWidth: 0.028, y1OfWidth: 0.125 },
  mobile: { x1: 0.65, y0OfHeight: 0.024, y1OfHeight: 0.122 },
};

export const getAutoTitleColor = (img, { compact = false } = {}) => {
  try {
    const { naturalWidth, naturalHeight } = img || {};
    if (!naturalWidth || !naturalHeight) return null;

    const scale = Math.min(1, SAMPLE_WIDTH / naturalWidth);
    const width = Math.max(1, Math.round(naturalWidth * scale));
    const height = Math.max(1, Math.round(naturalHeight * scale));

    let x1;
    let y0;
    let y1;
    if (compact) {
      const zone = HEADING_ZONE.mobile;
      x1 = width * zone.x1;
      y0 = height * zone.y0OfHeight;
      y1 = height * zone.y1OfHeight;
    } else {
      const zone = HEADING_ZONE.desktop;
      x1 = width * zone.x1;
      y0 = Math.min(height - 1, width * zone.y0OfWidth);
      y1 = Math.min(height, width * zone.y1OfWidth);
    }
    const zoneX = 0;
    const zoneY = Math.floor(y0);
    const zoneW = Math.max(1, Math.round(x1));
    const zoneH = Math.max(1, Math.round(y1) - zoneY);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(img, 0, 0, width, height);
    // Throws a SecurityError when the image was not loaded with CORS.
    const { data } = context.getImageData(zoneX, zoneY, zoneW, zoneH);

    let lumaSum = 0;
    let count = 0;
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] < 128) continue; // skip transparent pixels
      lumaSum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      count += 1;
    }
    if (!count) return null;

    return lumaSum / count >= DARK_TITLE_LUMA
      ? LIGHT_BG_TITLE_COLOR
      : DARK_BG_TITLE_COLOR;
  } catch (error) {
    return null;
  }
};
